import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  GET_ANALYTICS_LIST,
  GET_GRAPH,
  LIST_ALL_LOCATIONS,
} from "../graphql/queries";
import { GetGraphQuery } from "../graphql/queries/getGraph";
import {
  GetAnalyticsListQueryParameter,
  GetGraphQueryParameter,
  SearchableTestFilterInput,
} from "../graphql/types";
import useSiteTitle from "../hooks/useSiteTitle";
import { parseDate } from "../utils/tests";

// @ts-ignore

import { ApexOptions } from "apexcharts";
import moment from "moment";
import Chart from "react-apexcharts";
import AnalyticsTable from "../components/Dashboard/AnalyticsTable";
import FilterForm, {
  FilterFormFunction,
  FilterFormValues,
} from "../components/Dashboard/FilterForm";
import { LoadingIndicator } from "../components/Loading";
import { GetAnalyticsListQuery } from "../graphql/queries/getAnalyticsList";
import { ListAllLocationsQuery } from "../graphql/queries/listAllLocations";

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom" as const,
    },
    title: {
      display: false,
      text: "maT Analytics",
    },
  },
};

interface Props {}
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    paddingBottom: 60,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  filter: { marginTop: 40, marginBottom: 20 },
  button: {
    margin: theme.spacing(1),
  },
  items: {
    marginTop: 10,
  },
}));

function generateFilter({
  companyId,
  endDate,
  startDate,
  locationId,
  result,
  userId,
}: FilterFormValues): SearchableTestFilterInput | undefined {
  const and: Array<SearchableTestFilterInput> = [];
  if (companyId) {
    and.push({
      companyId: { eq: companyId },
    });
  }
  if (locationId) {
    and.push({
      locationId: { eq: locationId },
    });
  }
  if (userId) {
    and.push({
      userId: { eq: userId },
    });
  }
  if (endDate) {
    and.push({
      date: { lte: parseDate(endDate, true) },
    });
  }
  if (startDate) {
    and.push({
      date: { gte: parseDate(startDate) },
    });
  }
  if (result) {
    and.push({
      result: { eq: result },
    });
  }

  return {
    and,
  };
}

const areaChartOptions: Partial<ApexOptions> = {
  chart: {
    height: 450,
    toolbar: {
      show: false,
    },
  },
  xaxis: { categories: ["Testing", "Testing2"] },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    width: 2,
  },
  grid: {
    strokeDashArray: 0,
  },
};

const Dashboard: React.FC<Props> = () => {
  const classes = useStyles();
  const theme = useTheme();
  useSiteTitle("Dashboard");

  const [graphType, setGraphType] = useState<"bar" | "area">("area");

  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<{
    options: ApexOptions;
    series: ApexAxisChartSeries;
  }>({
    options: areaChartOptions,
    series: [
      {
        name: "Abgenommene Tests",
        data: [],
      },
    ],
  });
  const client = useApolloClient();

  const [fetchGraph, { data, loading }] = useLazyQuery<
    GetGraphQuery,
    GetGraphQueryParameter
  >(GET_GRAPH, {
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden des Graphen", {
        variant: "error",
      });
    },
  });

  const [fetchList, { data: listData, loading: listLoading }] = useLazyQuery<
    GetAnalyticsListQuery,
    GetAnalyticsListQueryParameter
  >(GET_ANALYTICS_LIST, {
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden der Standortliste", {
        variant: "error",
      });
    },
  });

  const { data: locationData } = useQuery<ListAllLocationsQuery>(
    LIST_ALL_LOCATIONS,
    {
      onError(error) {
        console.log(error);
      },
    }
  );

  const { getGraph } = data || {};
  const { listLocations } = locationData || {};
  const { items: locations = [] } = listLocations || {};
  const _handleFormSubmit: FilterFormFunction = (values) => {
    let filter = generateFilter(values);
    if (values.healthDeparmentNumber) {
      const addedLocations =
        locations?.filter(
          ({ healthDeparmentNumber }) =>
            values.healthDeparmentNumber === healthDeparmentNumber
        ) || [];
      const orLocationFilter = addedLocations.reduce<SearchableTestFilterInput>(
        (acc, current) => {
          return {
            ...acc,
            or: [
              ...(acc.or || []),
              {
                locationId: { eq: current.id },
              },
            ],
          };
        },
        { or: [] }
      );
      if (addedLocations.length > 0) {
        filter = { ...filter, and: [...(filter?.and || []), orLocationFilter] };
      } else {
        enqueueSnackbar(
          "Kein Standort wurde dieser Abrechnungsnummer zugeordnet",
          {
            variant: "warning",
          }
        );
        setState({
          options: areaChartOptions,
          series: [
            {
              name: "Abgenommene Tests",
              data: [],
            },
          ],
        });
        return Promise.resolve();
      }
    }

    fetchGraph({
      variables: {
        filter: filter!,
        startDate: parseDate(values.startDate!),
        endDate: parseDate(values.endDate!),
      },
    });

    if (values.companyId) {
      fetchList({
        variables: {
          filter: filter!,
        },
      });
    }
    return Promise.resolve();
  };

  useEffect(() => {
    const graphPoints = getGraph || [];
    setState((prevState) => ({
      options: {
        ...prevState.options,
        chart: {
          ...prevState.options.chart,
          type: graphType,
        },
        colors: [theme.palette.secondary.main],
        xaxis: {
          type: "category",
          axisBorder: {
            show: true,
            color: theme.palette.secondary.main,
          },
          labels: {
            style: {
              colors: theme.palette.primary.dark,
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: [theme.palette.secondary.dark],
            },
          },
        },

        grid: {
          borderColor: theme.palette.secondary.light,
        },
        tooltip: {
          theme: "light",
        },
      },
      series: [
        {
          name: "Abgenommene Tests",
          data: graphPoints.map(({ data, label }) => ({
            x: moment(label).format("DD.MM.YYYY"),
            y: data,
          })),
        },
      ],
    }));
  }, [theme, getGraph, graphType]);
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">Dashboard</Typography>
      </div>
      <FilterForm onSubmit={_handleFormSubmit} />
      <Grid
        item
        xs={12}
        md={12}
        container
        direction="row"
        alignItems="center"
        justify="flex-start"
        className={classes.items}
      >
        <Grid item xs={12} md={12} container spacing={3}>
          <Grid container alignItems="center" justify="space-between" item>
            <Grid item>
              <Typography variant="h5"> Anzahl an Testabnahmen</Typography>
            </Grid>
            <Grid item>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Button
                  size="small"
                  onClick={() => setGraphType("area")}
                  color={graphType === "area" ? "primary" : "secondary"}
                  variant={graphType === "area" ? "outlined" : "text"}
                >
                  Line
                </Button>
                <Button
                  size="small"
                  onClick={() => setGraphType("bar")}
                  color={graphType === "bar" ? "primary" : "secondary"}
                  variant={graphType === "bar" ? "outlined" : "text"}
                >
                  Bar
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid
            item
            component={Card}
            xs={12}
            container
            justify="center"
            style={{ minHeight: 200 }}
            alignItems="center"
          >
            {!loading && data && (
              <div style={{ width: "100%" }}>
                <Chart
                  options={state.options}
                  series={state.series}
                  type={graphType}
                  width={"100%"}
                  height={450}
                />
              </div>
            )}
            {loading && <LoadingIndicator />}
            {!loading && !data && <h5>Kein Daten zum erstellen des Graphes</h5>}
          </Grid>
          <Grid item style={{ marginTop: 20 }}>
            <Typography variant="h5">Aufschlüsselung</Typography>
          </Grid>
          <Grid item xs={12} container>
            <AnalyticsTable
              items={listData?.getList || []}
              aggregationLoading={listLoading}
            />
            {!listLoading && !listData && <h5>Kein Daten listen daten</h5>}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard;
